import { useState, useEffect, useRef } from "react";
import { ChevronDown, Search, X, SlidersHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import DatePresets from "@/components/ui/date-presets";
import DocTypeMultiSelect from "@/components/documents/DocTypeMultiSelect";

/**
 * LedgerFilterBar — compact collapsible filter row for LedgerDashboardPage.
 *
 * Props:
 *   filters        — current filter state object (includes portal: string)
 *   onFiltersChange(newFilters) — called on any change
 *   filterOptions  — { doctypes:[], currencies:[], asset_classes:[] } from data.filter_options
 *   fundNames      — string[] derived from all fund_name values (for fund single-select)
 *   portals        — portal objects [{ portal, funds[] }] for portal single-select
 *   defaultCollapsed — boolean (default true)
 */
export default function LedgerFilterBar({
  filters,
  onFiltersChange,
  filterOptions,
  fundNames = [],
  portals = [],
  defaultCollapsed = true,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const debounceRef = useRef(null);

  // Sync local search with external reset
  useEffect(() => {
    setSearchInput(filters.search || "");
  }, [filters.search]);

  // Debounce search field
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFiltersChange({ ...filters, search: searchInput });
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const currencies = filterOptions?.currencies || [];
  const assetClasses = filterOptions?.asset_classes || [];
  const docTypes = (filterOptions?.doctypes || []).map((t) =>
    typeof t === "string" ? { id: t, name: t } : t
  );

  const set = (key) => (val) => onFiltersChange({ ...filters, [key]: val });

  const handleClear = () => {
    setSearchInput("");
    onFiltersChange({
      portal: "",
      fund: "",
      investor: "",
      currency: "",
      assetClass: "",
      docTypes: [],
      dateFrom: "",
      dateTo: "",
      xirrMin: "",
      xirrMax: "",
      showExited: true,
      search: "",
    });
  };

  const hasActiveFilters =
    filters.portal ||
    filters.fund ||
    filters.investor ||
    filters.currency ||
    filters.assetClass ||
    (filters.docTypes && filters.docTypes.length > 0) ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.xirrMin ||
    filters.xirrMax ||
    filters.showExited === false ||
    filters.search;

  const activeCount = [
    filters.portal,
    filters.fund,
    filters.investor,
    filters.currency,
    filters.assetClass,
    filters.docTypes?.length > 0,
    filters.dateFrom,
    filters.dateTo,
    filters.xirrMin,
    filters.xirrMax,
    filters.showExited === false,
    filters.search,
  ].filter(Boolean).length;

  return (
    <Card className="p-3">
      {/* Header row — always visible */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
          aria-expanded={!collapsed}
          data-testid="filter-bar-toggle"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          <span className="font-medium">Filters</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">
              {activeCount}
            </span>
          )}
          <ChevronDown
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${collapsed ? "" : "rotate-180"}`}
          />
        </button>

        {/* Inline search always visible */}
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <Input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search funds or investors..."
            className="h-7 text-xs pl-8 w-full"
            data-testid="ledger-search-input"
          />
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="h-7 text-xs px-2 text-gray-500 gap-1 flex-shrink-0"
            data-testid="ledger-filter-clear"
          >
            <X className="w-3 h-3" />
            Clear
          </Button>
        )}
      </div>

      {/* Expandable filter controls */}
      {!collapsed && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-3 items-end">

          {/* Portal single-select — first control */}
          {portals.length > 0 && (
            <div className="min-w-[160px]">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Portal</p>
              <Select
                value={filters.portal || "__all__"}
                onValueChange={(v) => set("portal")(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="filter-portal-select">
                  <SelectValue placeholder="All portals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All portals</SelectItem>
                  {portals.map((p) => (
                    <SelectItem key={p.portal} value={p.portal}>
                      {p.displayName || p.portal}
                      {p.funds && (
                        <span className="ml-1.5 text-gray-400">({p.funds.length})</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Fund single-select */}
          {fundNames.length > 0 && (
            <div className="min-w-[160px]">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Fund</p>
              <Select
                value={filters.fund || "__all__"}
                onValueChange={(v) => set("fund")(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="filter-fund-select">
                  <SelectValue placeholder="All Funds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Funds</SelectItem>
                  {fundNames.map((name) => (
                    <SelectItem key={name} value={name} className="uppercase">{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Investor substring search */}
          <div className="min-w-[140px]">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Investor</p>
            <Input
              type="text"
              value={filters.investor || ""}
              onChange={(e) => onFiltersChange({ ...filters, investor: e.target.value })}
              placeholder="Investor name..."
              className="h-8 text-xs"
              data-testid="filter-investor-input"
            />
          </div>

          {/* Currency single-select */}
          {currencies.length > 0 && (
            <div className="min-w-[100px]">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Currency</p>
              <Select
                value={filters.currency || "__all__"}
                onValueChange={(v) => set("currency")(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="filter-currency-select">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  {currencies.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Asset class single-select */}
          {assetClasses.length > 0 && (
            <div className="min-w-[120px]">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Asset Class</p>
              <Select
                value={filters.assetClass || "__all__"}
                onValueChange={(v) => set("assetClass")(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="filter-assetclass-select">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  {assetClasses.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Doc type multiselect */}
          {docTypes.length > 0 && (
            <div className="min-w-[160px]">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Doc Type</p>
              <DocTypeMultiSelect
                selected={filters.docTypes || []}
                onChange={(v) => set("docTypes")(v)}
                docTypeOptions={docTypes}
              />
            </div>
          )}

          {/* Date From */}
          <div>
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Activity From</p>
            <Input
              type="date"
              value={filters.dateFrom || ""}
              onChange={(e) => set("dateFrom")(e.target.value)}
              className="h-8 text-xs w-[140px]"
              data-testid="filter-date-from"
            />
          </div>

          {/* Date To */}
          <div>
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Activity To</p>
            <Input
              type="date"
              value={filters.dateTo || ""}
              onChange={(e) => set("dateTo")(e.target.value)}
              className="h-8 text-xs w-[140px]"
              data-testid="filter-date-to"
            />
          </div>

          <DatePresets
            from={filters.dateFrom || ""}
            to={filters.dateTo || ""}
            onChange={(from, to) => onFiltersChange({ ...filters, dateFrom: from, dateTo: to })}
            className="pb-0.5"
          />

          {/* Return % min */}
          <div>
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Return % min</p>
            <Input
              type="number"
              value={filters.xirrMin || ""}
              onChange={(e) => set("xirrMin")(e.target.value)}
              placeholder="-20"
              className="h-8 text-xs w-[90px]"
              data-testid="filter-xirr-min"
            />
          </div>

          {/* Return % max */}
          <div>
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Return % max</p>
            <Input
              type="number"
              value={filters.xirrMax || ""}
              onChange={(e) => set("xirrMax")(e.target.value)}
              placeholder="30"
              className="h-8 text-xs w-[90px]"
              data-testid="filter-xirr-max"
            />
          </div>

          {/* Show exited toggle */}
          <div className="flex items-center gap-2 pb-1">
            <Checkbox
              id="show-exited"
              checked={filters.showExited !== false}
              onCheckedChange={(checked) => set("showExited")(checked !== false)}
              data-testid="filter-show-exited"
            />
            <label htmlFor="show-exited" className="text-xs text-gray-600 cursor-pointer select-none">
              Show exited
            </label>
          </div>
        </div>
      )}

      {/* Active doc-type chips */}
      {!collapsed && filters.docTypes && filters.docTypes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-100 items-center">
          {filters.docTypes.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-md bg-gray-100 text-gray-700 text-xs px-2 py-0.5"
            >
              {t}
              <button
                type="button"
                aria-label={`Remove ${t}`}
                onClick={() => set("docTypes")(filters.docTypes.filter((v) => v !== t))}
                className="hover:text-gray-900 cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}
